import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Post, PostData } from "@/components/Community/Post";
import { PostSkeleton } from "@/components/Community/PostSkeleton";
import { LatestJobs } from "@/components/Community/LatestJobs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Rss, FileText, Calendar, Briefcase, Headphones } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AppState } from "@/store";
import { extractHashtags, getCommunityPath } from "@/utils/community";
import {
  getPostById,
  updatePost,
  deletePost,
  reactPost,
  removeReactPost,
  createComment,
  updateComment,
  deleteComment,
  getCommentsByPost,
  registerEvent,
  unregisterEvent,
  cancelEvent as cancelEventAPI,
  sharePost,
} from "@/api/community";
import { useToast } from "@/components/ui/use-toast";
import { getSupportNotificationCount } from "@/components/Community/SupportPage";

export function PostDetails() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const [searchParams] = useSearchParams();

  const [post, setPost] = useState<PostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const previousPostIdRef = useRef<string | undefined>(undefined);

  // Get commentId and replyId from URL query params
  const commentId = searchParams.get("commentId");
  const replyId = searchParams.get("replyId");

  const userData = useSelector((state: AppState) => state.auth.userData);
  const user = userData?.user;
  const [supportReportCount] = useState(getSupportNotificationCount());

  // Helper function to transform API comments to PostData format
  const transformCommentsToPostFormat = (apiComments: any[]): any[] => {
    return apiComments.map((comment) => ({
      id: comment.id || comment._id,
      author: {
        id: comment.author?.id || comment.author_id,
        name: comment.author?.full_name || t("community.post_details.unknown_user"),
        avatar: comment.author?.photo_thumbnail_url || comment.author?.photo || "",
        title: comment.author?.job_title
          ? comment.author.job_title
          : comment.author?.type === "job_seeker"
            ? t("community.post_details.jobseeker")
            : comment.author?.type === "employer"
              ? t("community.post_details.employer")
              : "",
        type: comment.author?.type,
        organizationId: comment.author?.organization_id,
      },
      content: comment.content,
      timestamp: new Date(comment.created_at),
      isEdited: Array.isArray(comment.edit_history) && comment.edit_history.length > 0,
      editedAt: comment.edit_history?.[0]?.edited_at
        ? new Date(comment.edit_history[0].edited_at)
        : undefined,
      likes: [],
      replies: comment.replies ? transformCommentsToPostFormat(comment.replies) : [],
    }));
  };

  // Fetch post by ID
  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) {
        setError(t("community.post_details.post_id_not_found"));
        setIsLoading(false);
        return;
      }

      // Only show loading skeleton when postId changes, not when commentId/replyId change
      const isPostIdChanged = previousPostIdRef.current !== postId;
      if (isPostIdChanged) {
        setIsLoading(true);
      }
      previousPostIdRef.current = postId;

      try {
        setError(null);

        const apiPost = await getPostById(postId);

        // Fetch comments for this post (same approach as feed)
        let comments: any[] = [];
        try {
          if (apiPost.comments_count > 0) {
            const commentsData = await getCommentsByPost(apiPost.id, 1, 50);
            // Transform API comments to PostData comment format
            comments = transformCommentsToPostFormat(commentsData.comments);
          }
        } catch (error) {
          console.error(`Error fetching comments for post ${apiPost.id}:`, error);
        }

        // Transform shared_post or original_post to flat structure
        // Use original_post if available (for nested shares), otherwise use shared_post
        let transformedSharedPost = undefined;
        const sourcePost = apiPost.original_post || apiPost.shared_post;

        // Check if this is a nested share with missing original post
        const isNestedShareWithMissingOriginal =
          !apiPost.original_post &&
          apiPost.shared_post &&
          (apiPost.shared_post.type === "shared" || apiPost.shared_post.type === "shared_post") &&
          !apiPost.shared_post.content;

        if (sourcePost && sourcePost.author && !isNestedShareWithMissingOriginal) {
          transformedSharedPost = {
            full_name: sourcePost.author.full_name,
            email: sourcePost.author.email,
            type: sourcePost.author.type,
            photo_thumbnail_url: sourcePost.author.photo_thumbnail_url,
            job_title: sourcePost.author.job_title,
            id: sourcePost.id || sourcePost.author.id, // Use post ID, fallback to author ID
            author_id: sourcePost.author.id, // Original post author ID
            content: sourcePost.content,
            media_urls: sourcePost.media_urls,
            tagged_users: sourcePost.tagged_users,
            deleted: sourcePost.deleted,
            deleted_at: sourcePost.deleted_at ? new Date(sourcePost.deleted_at) : undefined,
            // Include event details for shared event posts
            isEvent: sourcePost.is_event || sourcePost.type === "event",
            eventDetails:
              (sourcePost.is_event || sourcePost.type === "event") && sourcePost.event_details
                ? {
                    title: sourcePost.event_details.title,
                    date: new Date(sourcePost.event_details.date),
                    endDate: sourcePost.event_details.end_date
                      ? new Date(sourcePost.event_details.end_date)
                      : undefined,
                    location: sourcePost.event_details.location,
                    maxParticipants: sourcePost.event_details.max_participants,
                    description: sourcePost.event_details.description,
                  }
                : undefined,
            eventStatus: sourcePost.event_status as "active" | "cancelled" | undefined,
            eventParticipants: sourcePost.event_participants
              ? sourcePost.event_participants
                  .filter(
                    (participant: any) =>
                      participant.user !== null && participant.user !== undefined,
                  )
                  .map((participant: any) => ({
                    userId: participant.user.id,
                    userName: participant.user.full_name,
                    userTitle:
                      participant.user.job_title ||
                      (participant.user.type === "job_seeker"
                        ? t("community.post_details.job_seeker")
                        : t("community.post_details.employer")),
                    userType: participant.user.type,
                    registeredAt: new Date(participant.registered_at),
                  }))
              : [],
          };
        }

        const transformedPost: PostData = {
          ...apiPost,
          timestamp: new Date(apiPost.created_at),
          isEdited: apiPost.edit_history && apiPost.edit_history.length > 0,
          editedAt: apiPost.edit_history?.[0]?.edited_at
            ? new Date(apiPost.edit_history[0].edited_at)
            : undefined,
          likes: [],
          comments,
          shared_post: transformedSharedPost as any,
          // Handle shared posts
          isShared: apiPost.type === "shared_post",
          originalPostId: apiPost.shared_post_id,
          sharedAt: apiPost.type === "shared_post" ? new Date(apiPost.created_at) : undefined,
          // Handle event posts
          isEvent: apiPost.type === "event",
          eventDetails:
            apiPost.type === "event" && apiPost.event_details
              ? {
                  title: apiPost.event_details.title,
                  date: new Date(apiPost.event_details.date),
                  endDate: apiPost.event_details.end_date
                    ? new Date(apiPost.event_details.end_date)
                    : undefined,
                  location: apiPost.event_details.location,
                  maxParticipants: apiPost.event_details.max_participants,
                  description: apiPost.event_details.description,
                }
              : undefined,
          eventStatus: apiPost.event_status as "active" | "cancelled" | undefined,
          eventParticipants: apiPost.event_participants
            ? apiPost.event_participants
                .filter(
                  (participant: any) => participant.user !== null && participant.user !== undefined,
                )
                .map((participant: any) => ({
                  userId: participant.user.id,
                  userName: participant.user.full_name,
                  userTitle:
                    participant.user.job_title ||
                    (participant.user.type === "job_seeker"
                      ? t("community.post_details.job_seeker")
                      : t("community.post_details.employer")),
                  userType: participant.user.type,
                  registeredAt: new Date(participant.registered_at),
                }))
            : [],
        };

        setPost(transformedPost);
      } catch (err: any) {
        console.error("Error fetching post:", err);

        // Check if it's a 404 error
        if (err?.response?.status === 404 || err?.error?.code === 404) {
          setError(t("community.post_details.post_not_found"));
        } else {
          setError(t("community.post_details.failed_load_post"));
        }
      } finally {
        // Only set loading to false if we set it to true (postId changed)
        if (isPostIdChanged) {
          setIsLoading(false);
        }
      }
    };

    fetchPost();
  }, [postId, commentId, replyId]);

  const handleLike = async (postId: string, emoji: string) => {
    if (!user?.id || !post) return;

    const existingLike = post.likes?.find((like) => like.userId === user.id);

    try {
      if (existingLike && existingLike.emoji === emoji) {
        // Same emoji clicked - unlike the post
        await removeReactPost(postId);
        // Update local state optimistically
        const newLikes = post.likes?.filter((like) => like.userId !== user.id) || [];
        setPost({
          ...post,
          likes: newLikes,
          likes_count: Math.max(0, post.likes_count - 1),
        });
      } else {
        // Different emoji or new like
        await reactPost(postId, { emoji });
        // Update local state optimistically
        const newLikes = post.likes?.filter((like) => like.userId !== user.id) || [];
        const isNewReaction = !existingLike;
        setPost({
          ...post,
          likes: [
            ...newLikes,
            {
              userId: user.id,
              userName: user.full_name || t("community.post_details.unknown_user"),
              emoji,
            },
          ],
          likes_count: isNewReaction ? post.likes_count + 1 : post.likes_count,
        });
      }
    } catch (error) {
      console.error("Error updating reaction:", error);
      toast({
        title: t("community.post_details.failed_reaction"),
        description: t("community.post_details.failed_reaction_desc"),
        variant: "destructive",
      });
    }
  };

  const handleComment = async (
    postId: string,
    comment: string,
    mentionedUserIds: string[] = [],
  ) => {
    if (!post) return;

    try {
      const newComment = await createComment({
        post_id: postId,
        content: comment,
        mentioned_users: mentionedUserIds,
      });

      // Transform the API comment to UI format
      const transformedComment = transformCommentsToPostFormat([newComment])[0];

      // Update local state with the new comment
      setPost({
        ...post,
        comments: [...(post.comments || []), transformedComment],
        comments_count: post.comments_count + 1,
      });

      toast({
        title: t("community.post_details.comment_added"),
        description: t("community.post_details.comment_added_desc"),
      });
    } catch (error) {
      console.error("Error creating comment:", error);
      toast({
        title: t("community.post_details.failed_add_comment"),
        description: t("community.post_details.failed_add_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleShare = async (postId: string) => {
    if (!user?.id) return;

    try {
      await sharePost(postId, {
        share_content: "",
      });

      toast({
        title: t("community.post_details.post_shared_feed"),
        description: t("community.post_details.post_shared_feed_desc"),
      });
    } catch (error) {
      console.error("Error sharing post:", error);
      toast({
        title: t("community.post_details.failed_share_post"),
        description: t("community.post_details.failed_share_post_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (
    postId: string,
    updates: {
      content: string;
      media_urls: string[];
      tagged_users: string[];
      eventDetails?: {
        title: string;
        date: Date;
        endDate?: Date;
        location: string;
        maxParticipants?: number;
        description?: string;
      };
    },
  ) => {
    if (!post) return;

    try {
      const hashtags = extractHashtags(updates.content);

      const payload: any = {
        content: updates.content,
        visibility: "public",
        hashtag_names: hashtags,
        media_urls: updates.media_urls,
        tagged_users: updates.tagged_users,
      };

      // Event posts save their details alongside the post body in one request.
      if (updates.eventDetails) {
        payload.event_status = post.eventStatus === "cancelled" ? "cancelled" : "active";
        payload.event_details = {
          title: updates.eventDetails.title,
          date: updates.eventDetails.date.toISOString(),
          location: updates.eventDetails.location,
          ...(updates.eventDetails.endDate && {
            end_date: updates.eventDetails.endDate.toISOString(),
          }),
          ...(updates.eventDetails.maxParticipants && {
            max_participants: updates.eventDetails.maxParticipants,
          }),
          ...(updates.eventDetails.description && {
            description: updates.eventDetails.description,
          }),
        };
      }

      await updatePost(postId, payload);

      // Fetch the updated post to get new signed URLs for attachments
      const freshPost = await getPostById(postId);

      // Update the post with fresh attachment URLs and updated content, keeping existing comments and likes
      setPost({
        ...post,
        content: freshPost.content,
        media_urls: freshPost.media_urls,
        "media_urls:raw": freshPost["media_urls:raw"],
        tagged_users: freshPost.tagged_users,
        hashtags: freshPost.hashtags,
        ...(updates.eventDetails && { eventDetails: updates.eventDetails }),
        isEdited: true,
        editedAt: new Date(freshPost.updated_at),
        edit_history: freshPost.edit_history,
        updated_at: freshPost.updated_at,
      });

      toast({
        title: t("community.post_details.post_updated"),
        description: t("community.post_details.post_updated_desc"),
      });
    } catch (error: any) {
      console.error("Error updating post:", error);
      toast({
        title: t("community.post_details.failed_update_post"),
        description:
          error.response?.data?.message || t("community.post_details.failed_update_post_desc"),
        variant: "destructive",
      });
      // Rethrow so the Post edit surface stays open with the user's changes.
      throw error;
    }
  };

  const handleDelete = async (
    postId: string,
    reason?: string,
    postType?: string,
    isEvent?: boolean,
  ) => {
    try {
      // Determine if this is an event post from multiple sources

      if (isEvent) {
        // For event posts, use updatePost API to set visibility to private
        await updatePost(postId, {
          content: post?.content || "",
          visibility: "private",
        });

        toast({
          title: t("community.post_details.event_post_deleted"),
          description: t("community.post_details.event_post_deleted_desc"),
        });
      } else {
        // For regular posts, use deletePost API
        const deleteReason = reason || "Post removed by user";
        await deletePost(postId, { reason: deleteReason });

        toast({
          title: t("community.post_details.post_deleted"),
          description: t("community.post_details.post_deleted_desc"),
        });
      }

      // Navigate back to the feed
      navigate(getCommunityPath(user?.type));
    } catch (error) {
      console.error("Error deleting post:", error);
      toast({
        title: t("community.post_details.failed_delete_post"),
        description: t("community.post_details.failed_delete_post_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEditComment = async (
    _postId: string,
    commentId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    if (!post) return;

    try {
      await updateComment(commentId, { content: newContent, mentioned_users: mentionedUserIds });

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.map((comment) =>
          comment.id === commentId
            ? { ...comment, content: newContent, isEdited: true, editedAt: new Date() }
            : comment,
        ),
      });

      toast({
        title: t("community.post_details.comment_updated"),
        description: t("community.post_details.comment_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating comment:", error);
      toast({
        title: t("community.post_details.failed_update_comment"),
        description: t("community.post_details.failed_update_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (_postId: string, commentId: string) => {
    if (!post) return;

    try {
      await deleteComment(commentId, "Deleted by user");

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.filter((comment) => comment.id !== commentId),
        comments_count: Math.max(0, post.comments_count - 1),
      });

      toast({
        title: t("community.post_details.comment_deleted"),
        description: t("community.post_details.comment_deleted_desc"),
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: t("community.post_details.failed_delete_comment"),
        description: t("community.post_details.failed_delete_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleAddReply = async (
    postId: string,
    commentId: string,
    reply: string,
    mentionedUserIds: string[] = [],
  ) => {
    // CommentSection handles reply creation and state update directly
    // This handler is intentionally empty to avoid duplicate API calls
    // The reply is already created and managed by CommentSection.tsx
  };

  const handleEditReply = async (
    _postId: string,
    commentId: string,
    replyId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    if (!post) return;

    try {
      await updateComment(replyId, { content: newContent, mentioned_users: mentionedUserIds });

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,
              replies: comment.replies.map((reply: any) =>
                reply.id === replyId
                  ? { ...reply, content: newContent, isEdited: true, editedAt: new Date() }
                  : reply,
              ),
            };
          }
          return comment;
        }),
      });

      toast({
        title: t("community.post_details.reply_updated"),
        description: t("community.post_details.reply_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating reply:", error);
      toast({
        title: t("community.post_details.failed_update_reply"),
        description: t("community.post_details.failed_update_reply_desc"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteReply = async (_postId: string, commentId: string, replyId: string) => {
    if (!post) return;

    try {
      await deleteComment(replyId, "Deleted by user");

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,
              replies: comment.replies.filter((reply: any) => reply.id !== replyId),
            };
          }
          return comment;
        }),
      });

      toast({
        title: t("community.post_details.reply_deleted"),
        description: t("community.post_details.reply_deleted_desc"),
      });
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast({
        title: t("community.post_details.failed_delete_reply"),
        description: t("community.post_details.failed_delete_reply_desc"),
        variant: "destructive",
      });
    }
  };

  const handleJoinEvent = async (postId: string) => {
    if (!user?.id || !post) return;

    const alreadyRegistered = post.eventParticipants?.some((p) => p.userId === user.id);
    if (alreadyRegistered) {
      toast({
        title: t("community.post_details.already_registered"),
        description: t("community.post_details.already_registered_desc"),
        variant: "destructive",
      });
      return;
    }

    if (
      post.eventDetails?.maxParticipants &&
      (post.eventParticipants?.length || 0) >= post.eventDetails.maxParticipants
    ) {
      toast({
        title: t("community.post_details.event_full"),
        description: t("community.post_details.event_full_desc"),
        variant: "destructive",
      });
      return;
    }

    try {
      await registerEvent(postId);

      const newParticipant = {
        userId: user.id,
        userName: user.full_name || t("community.post_details.unknown_user"),
        userTitle:
          user.type === "employer"
            ? t("community.post_details.employer")
            : t("community.post_details.job_seeker"),
        registeredAt: new Date(),
      };

      setPost({
        ...post,
        eventParticipants: [...(post.eventParticipants || []), newParticipant],
      });

      toast({
        title: t("community.post_details.successfully_registered"),
        description: t("community.post_details.successfully_registered_desc"),
      });
    } catch (error) {
      console.error("Error joining event:", error);
      toast({
        title: t("community.post_details.failed_join_event"),
        description: t("community.post_details.failed_join_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleLeaveEvent = async (postId: string) => {
    if (!user?.id || !post) return;

    try {
      await unregisterEvent(postId);

      setPost({
        ...post,
        eventParticipants: (post.eventParticipants || []).filter((p) => p.userId !== user.id),
      });

      toast({
        title: t("community.post_details.successfully_left_event"),
        description: t("community.post_details.successfully_left_event_desc"),
      });
    } catch (error) {
      console.error("Error leaving event:", error);
      toast({
        title: t("community.post_details.failed_leave_event"),
        description: t("community.post_details.failed_leave_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleCancelEvent = async (postId: string) => {
    if (!post) return;

    try {
      await cancelEventAPI(postId);

      setPost({
        ...post,
        eventStatus: "cancelled" as const,
      });

      toast({
        title: t("community.post_details.event_cancelled"),
        description: t("community.post_details.event_cancelled_desc"),
      });
    } catch (error) {
      console.error("Error cancelling event:", error);
      toast({
        title: t("community.post_details.failed_cancel_event"),
        description: t("community.post_details.failed_cancel_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEditEvent = async (
    postId: string,
    eventDetails: {
      title: string;
      date: Date;
      endDate?: Date;
      location: string;
      maxParticipants?: number;
      description?: string;
    },
  ) => {
    if (!post) return;

    try {
      const payload: any = {
        content: post.content,
        event_status: post.eventStatus === "cancelled" ? "cancelled" : "active",
        event_details: {
          title: eventDetails.title,
          date: eventDetails.date.toISOString(),
          location: eventDetails.location,
          ...(eventDetails.endDate && { end_date: eventDetails.endDate.toISOString() }),
          ...(eventDetails.maxParticipants && { max_participants: eventDetails.maxParticipants }),
          ...(eventDetails.description && { description: eventDetails.description }),
        },
      };

      await updatePost(postId, payload);

      setPost({
        ...post,
        eventDetails,
        isEdited: true,
        editedAt: new Date(),
      });

      toast({
        title: t("community.post_details.event_updated"),
        description: t("community.post_details.event_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating event:", error);
      toast({
        title: t("community.post_details.failed_update_event"),
        description: t("community.post_details.failed_update_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleBackToFeed = () => {
    navigate(getCommunityPath(user?.type));
  };

  const sidebarNavItems = [
    { id: "feed", label: t("community.post_details.nav_feed"), icon: Rss },
    { id: "my-posts", label: t("community.post_details.nav_my_posts"), icon: FileText },
    { id: "events", label: t("community.post_details.nav_events"), icon: Calendar },
    {
      id: "support",
      label: t("community.post_details.nav_support"),
      icon: Headphones,
      badge: supportReportCount,
    },
    { id: "latest-jobs", label: t("community.post_details.nav_latest_jobs"), icon: Briefcase },
  ];

  return (
    <main data-community-container className="mx-auto w-full max-w-(--breakpoint-2xl)">
      <div className="">
        {/* Mobile Navigation Pills - Visible only on mobile/tablet */}
        <div className="mb-4 xl:hidden">
          <Button variant="outline" size="sm" className="gap-2" onClick={handleBackToFeed}>
            <ArrowLeft className="h-4 w-4" />
            <span className="font-semibold">{t("community.post_details.back_to_feed")}</span>
          </Button>
        </div>

        <div className="flex gap-4 lg:gap-6">
          {/* Left Sidebar Navigation - Fixed on desktop */}
          <aside className="hidden w-64 shrink-0 xl:block">
            <div className="fixed top-32 w-64">
              <div className="brand-gradient-deep overflow-hidden rounded-2xl border border-white/10 shadow-md">
                <nav aria-label={t("community.feed.title")} className="flex flex-col gap-1 p-3">
                  {sidebarNavItems
                    .filter((item) => item.id !== "latest-jobs")
                    .map((item) => {
                      const Icon = item.icon;
                      const badgeCount = "badge" in item ? item.badge : 0;

                      return (
                        <Button
                          key={item.id}
                          variant="ghost"
                          className="group relative h-auto w-full justify-start gap-3.5 rounded-xl px-3.5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
                          onClick={() => {
                            navigate(`${getCommunityPath(user?.type)}?section=${item.id}`);
                          }}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          <span className="truncate">{item.label}</span>
                          {badgeCount > 0 && (
                            <span className="bg-destructive ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
                              {badgeCount}
                            </span>
                          )}
                        </Button>
                      );
                    })}
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="w-full min-w-0 flex-1 space-y-4 sm:space-y-8 lg:w-auto">
            <div className="space-y-1 sm:space-y-2">
              <div className="flex items-start gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden xl:flex"
                  onClick={handleBackToFeed}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                {/* Heading only is md:hidden — the desktop top bar shows it instead.
                    Keep it off the wrapper so the xl back arrow stays reachable. */}
                <div className="space-y-1 md:hidden">
                  <h1 className="text-foreground text-xl font-bold tracking-tight sm:text-2xl">
                    <span className="gradient-text">{t("community.post_details.title")}</span>
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    {t("community.post_details.description")}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-0 space-y-6">
              {isLoading ? (
                <PostSkeleton />
              ) : error ? (
                <Card className="border-border">
                  <div className="flex flex-col items-center gap-3 py-14 text-center">
                    <div className="bg-destructive/10 text-destructive flex h-12 w-12 items-center justify-center rounded-full">
                      <FileText className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-destructive text-base font-semibold">
                        {t("community.post_details.error_loading_post")}
                      </p>
                      <p className="text-muted-foreground text-sm">{error}</p>
                    </div>
                    <Button onClick={handleBackToFeed} variant="outline">
                      {t("community.post_details.back_to_feed")}
                    </Button>
                  </div>
                </Card>
              ) : post ? (
                <Post
                  post={post}
                  currentUserId={user?.id}
                  onLike={handleLike}
                  onComment={handleComment}
                  onShare={handleShare}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onEditComment={handleEditComment}
                  onDeleteComment={handleDeleteComment}
                  onAddReply={handleAddReply}
                  onEditReply={handleEditReply}
                  onDeleteReply={handleDeleteReply}
                  onJoinEvent={handleJoinEvent}
                  onLeaveEvent={handleLeaveEvent}
                  onCancelEvent={handleCancelEvent}
                  onEditEvent={handleEditEvent}
                  showCommentsInitially={!!commentId}
                  autoExpandCommentId={commentId || undefined}
                  highlightReplyId={replyId || undefined}
                />
              ) : null}
            </div>
          </div>

          {/* Right Sidebar - Job Posts */}
          {user?.type === "job_seeker" && (
            <aside className="hidden w-80 shrink-0 lg:block">
              <div className="">
                <LatestJobs />
              </div>
            </aside>
          )}
        </div>
      </div>
    </main>
  );
}
